import axiosClient from "@/utils/axios-client";
import { toCamelKeyObject, toSnakeKeyObject } from "@/utils/case";
import {
  PathPickProductRequestParams,
  PathPickRequestDetailParams,
} from "@/features/pathpick/types/request-params";
import {
  PathPickAutocompleteSuggestionResponse,
  PathPickCountsResponse,
  PathPickDisconnectJob,
  PathPickKreamOptionsResponse,
  PathPickPoizonOptionsResponse,
  PathPickProductIdsResponse,
  PathPickProductsResponse,
  PathPickRequestDetailResponse,
  PathPickUsageResponse,
} from "@/features/pathpick/types/response";

const url = {
  getPathPickProducts: (companyId: number) =>
    `/buyers/${companyId}/pathpick/products`,
  getPathPickCounts: (companyId: number) =>
    `/buyers/${companyId}/pathpick/counts`,
  getPathPickUsage: (companyId: number) =>
    `/buyers/${companyId}/pathpick/usage`,
  getPathPickRequestDetail: (companyId: number, requestId: number) =>
    `/buyers/${companyId}/pathpick/requests/${requestId}`,
  getKreamOptions: (kreamProductId: number) =>
    `/kream/products/${kreamProductId}/options`,
  // 경로 파라미터는 포이즌 상품의 spuId(globalSpuId)다.
  getPoizonOptions: (spuId: number) => `/poizon/products/${spuId}/options`,
  getPathPickAutocomplete: (companyId: number) =>
    `/buyers/${companyId}/pathpick/autocomplete`,
  getPathPickDisconnectJob: (companyId: number, jobId: string) =>
    `/buyers/${companyId}/pathpick/requests/disconnect-jobs/${jobId}`,
};

// pathpick 공용 조회(GET) API — 하위 feature 전용 API는 features/*/api에 둔다
export const pathPickQueryApi = {
  // 연동 해제 작업 상태 — COMPLETED/FAILED 가 될 때까지 해제 훅이 주기적으로 조회한다.
  getPathPickDisconnectJob: async (companyId: number, jobId: string) => {
    const { data } = await axiosClient.get(
      url.getPathPickDisconnectJob(companyId, jobId)
    );
    return toCamelKeyObject(data) as PathPickDisconnectJob;
  },
  // 이번 달 사용량 — 상품 등록(건수) / 스탁오더(금액) 한도
  getPathPickUsage: async (companyId: number) => {
    const { data } = await axiosClient.get<PathPickUsageResponse>(
      url.getPathPickUsage(companyId)
    );
    return data;
  },
  // GNB 배지용 카운트만 조회 — 목록 API로 개수만 얻으면 두 소스 조회·크림 조인까지 다 돌아 느리다.
  getPathPickCounts: async (companyId: number) => {
    const { data } = await axiosClient.get<PathPickCountsResponse>(
      url.getPathPickCounts(companyId)
    );
    return data;
  },
  // 상품 조회 — 상품 찾기 목록과 담긴 상품(isInBasket) 조회가 공용으로 사용
  getPathPickProducts: async (
    companyId: number,
    params?: PathPickProductRequestParams
  ) => {
    const { data } = await axiosClient.get<PathPickProductsResponse>(
      url.getPathPickProducts(companyId),
      { params: params ? toSnakeKeyObject(params) : undefined }
    );
    return data;
  },
  // 전체 선택용 — 같은 조건의 상품 id·유형만 페이지 없이 조회한다(ids_only, 최대 10,000건).
  getPathPickProductIds: async (
    companyId: number,
    params?: Omit<PathPickProductRequestParams, "idsOnly">
  ) => {
    const { data } = await axiosClient.get<PathPickProductIdsResponse>(
      url.getPathPickProducts(companyId),
      { params: toSnakeKeyObject({ ...params, idsOnly: true }) }
    );
    return data;
  },
  // 연동/다운로드 요청 단건 상세 (요청 헤더 + 상품별 처리 항목) — 등록 신청 내역의 요청 행과
  // 상품 카드의 "확인 필요" 배지가 같은 엔드포인트로 요청 상세 모달을 연다.
  getPathPickRequestDetail: async (
    companyId: number,
    requestId: number,
    params?: PathPickRequestDetailParams
  ) => {
    const { data } = await axiosClient.get<PathPickRequestDetailResponse>(
      url.getPathPickRequestDetail(companyId, requestId),
      { params: params ? toSnakeKeyObject(params) : undefined }
    );
    return data;
  },
  // 사이즈별 크림 시세 — 상세 모달을 열 때 그 상품 하나만. 목록에는 싣지 않는다.
  getKreamOptions: async (kreamProductId: number) => {
    const { data } = await axiosClient.get<PathPickKreamOptionsResponse>(
      url.getKreamOptions(kreamProductId)
    );
    return data;
  },
  // 사이즈별 포이즌 시세 — 크림과 같은 시점(상세 진입)에 그 상품 하나만 조회한다.
  getPoizonOptions: async (spuId: number) => {
    const { data } = await axiosClient.get<PathPickPoizonOptionsResponse>(
      url.getPoizonOptions(spuId)
    );
    return data;
  },
  // 검색창 자동완성(브랜드 + 단품오더 SKU). q 는 검색창 입력값 그대로.
  // brandIds 는 SKU 제안을 좁힐 브랜드 — 서버가 SKU 제안을 그 브랜드로 좁힌다(배열은 repeat 로 직렬화).
  getPathPickAutocomplete: async (
    companyId: number,
    q: string,
    brandIds?: number[]
  ) => {
    const { data } = await axiosClient.get<
      PathPickAutocompleteSuggestionResponse[]
    >(url.getPathPickAutocomplete(companyId), {
      params: { q, brand_id__in: brandIds?.length ? brandIds : undefined },
    });
    return data;
  },
};
